import React, { useEffect, useMemo, useState } from 'react';
import { auth } from '../../config/firebase';
import useAdminStore from '../../store/adminStore';
import useChangeRadarStore from '../../store/changeRadarStore';
import PEChangeRadarModal from './PEChangeRadarModal';

type Props = {
    /** true = alle Tickets; false = Coach-Filter */
    isAdmin?: boolean;
};

const IconBase = ({ children, size = 20, className = '' }: any) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
    >
        {children}
    </svg>
);

const RadarIcon = (props: any) => (
    <IconBase {...props}>
        <path d="M19.07 4.93A10 10 0 0 0 6.99 3.34" />
        <path d="M4 6h.01" />
        <path d="M2.29 9.62A10 10 0 1 0 21.31 8.35" />
        <path d="M16.24 7.76A6 6 0 1 0 8.58 16.39" />
        <path d="M12 12h.01" />
    </IconBase>
);

/**
 * Change-Radar Sidebar-Badge — Puls rose-500 bei pending > 0.
 */
const ChangeRadarBadge: React.FC<Props> = ({ isAdmin = false }) => {
    const {
        pendingRequests,
        completedRequests,
        isLoading,
        error,
        isModalOpen,
        openModal,
        closeModal,
        subscribe,
        unsubscribe,
    } = useChangeRadarStore();

    const adminTeam = useAdminStore((s) => s.adminTeam);
    const [activeTab, setActiveTab] = useState<'offen' | 'erledigt'>('offen');

    const coachKeys = useMemo(() => {
        const user = auth.currentUser;
        const email = (user?.email || '').trim().toLowerCase();
        const fromTeam = (adminTeam || []).find(
            (m) => (m.email || '').trim().toLowerCase() === email
        );
        const keys = [
            fromTeam?.id,
            fromTeam?.name,
            user?.uid,
            email,
            user?.displayName,
        ].filter(Boolean) as string[];
        return Array.from(new Set(keys.map((k) => String(k).trim()).filter(Boolean)));
    }, [adminTeam]);

    const coachKeysKey = coachKeys.join('|');

    useEffect(() => {
        subscribe({ isAdmin: !!isAdmin, coachKeys });
        return () => unsubscribe();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAdmin, coachKeysKey]);

    const count = pendingRequests.length;
    const hasPending = count > 0;

    return (
        <>
            <button
                type="button"
                onClick={() => {
                    setActiveTab('offen');
                    openModal();
                }}
                className={`w-full flex items-center justify-between px-4 py-3 rounded-lg border transition-all shadow-lg group ${
                    hasPending
                        ? 'bg-rose-50 border-rose-500 text-rose-800 hover:bg-rose-100 dark:bg-rose-900/10 dark:border-rose-500/50 dark:hover:bg-rose-900/20 dark:text-rose-400'
                        : 'bg-pe-card border-pe-border-strong text-pe-text hover:bg-pe-hover'
                }`}
            >
                <div className="flex items-center gap-3">
                    <RadarIcon
                        size={18}
                        className={hasPending ? 'text-rose-600 dark:text-rose-500 animate-pulse' : 'text-pe-muted'}
                    />
                    <span
                        className={`text-sm font-bold ${
                            hasPending ? 'text-rose-800 dark:text-rose-400' : 'text-pe-text'
                        }`}
                    >
                        Change Radar
                    </span>
                </div>
                {hasPending && (
                    <span className="bg-rose-500 text-white text-[10px] font-black px-2 py-0.5 rounded shadow-[0_0_10px_rgba(244,63,94,0.5)]">
                        {count}
                    </span>
                )}
            </button>

            <PEChangeRadarModal
                isOpen={isModalOpen}
                activeTab={activeTab}
                setActiveTab={setActiveTab}
                pendingRequests={pendingRequests}
                completedRequests={completedRequests}
                isLoading={isLoading}
                error={error}
                busyId={null}
                onClose={closeModal}
            />
        </>
    );
};

export default ChangeRadarBadge;
